import type { GridSortModel } from "@mui/x-data-grid";
import { z } from "zod";

import { libraryListSettingsSchema } from "api/schemas/Library";
import { PatientsListFiltersSchema } from "routes/patients/PatientsList/PatientsFilter/useFilters";

const ThirdPartyType: z.ZodType<GridSortModel> = z.any();

type StorageOptions = {
  keepOnClear: boolean;
};

function createStorageDefinition<Type>(schema: z.ZodType<Type>, options: StorageOptions = { keepOnClear: false }) {
  return { schema, options };
}

export const libraryListSettingsStorageDefinitions = {
  exercise_library_list_settings: createStorageDefinition(libraryListSettingsSchema),
  lesson_library_list_settings: createStorageDefinition(libraryListSettingsSchema),
  template_library_list_settings: createStorageDefinition(libraryListSettingsSchema),
};

export type LibraryListSettingsStorageKey = keyof typeof libraryListSettingsStorageDefinitions;

export const storageDefinitions = {
  calendar_settings: createStorageDefinition(
    z.object({
      show_my_availability: z.boolean(),
      show_others_availability: z.boolean(),
      show_canceled: z.boolean(),
      show_rescheduled: z.boolean(),
      show_weekends: z.boolean(),
    }),
    { keepOnClear: true }
  ),
  collapsedSidebar: createStorageDefinition(z.enum(["collapsed"])),
  hideSideContainer: createStorageDefinition(z.enum(["hide"])),
  language: createStorageDefinition(z.string(), { keepOnClear: true }),
  "login-remember-pn": createStorageDefinition(z.string(), { keepOnClear: true }),
  "login-remember-email": createStorageDefinition(z.string(), { keepOnClear: true }),
  "medical-notes-sort": createStorageDefinition(ThirdPartyType),
  patient_list_filters: createStorageDefinition(PatientsListFiltersSchema),
  patient_list_visible_columns: createStorageDefinition(
    z.object({
      id: z.boolean().optional(),
      name: z.boolean().optional(),
      icons: z.boolean().optional(),
      ailment: z.boolean().optional(),
      week: z.boolean().optional(),
      status: z.boolean().optional(),
      last_activity_completed_at: z.boolean().optional(),
      adherence: z.boolean().optional(),
      coverage: z.boolean().optional(),
      subscription: z.boolean().optional(),
      address_state: z.boolean().optional(),
    })
  ),
  "tours-completed": createStorageDefinition(
    z.object({
      appointment_details: z.boolean().optional(),
    })
  ),
  invitations_date_filter: createStorageDefinition(
    z.enum(["last_30_days", "last_3_months", "last_6_months", "last_12_months", "all"]),
    {
      keepOnClear: true,
    }
  ),
  ...libraryListSettingsStorageDefinitions,
};

export type StorageKey = keyof typeof storageDefinitions;

export type StorageValue<T extends StorageKey> = z.infer<typeof storageDefinitions[T]["schema"]>;
